import {Analytics, AnalyticsPlatform} from '@buncha/config/analytics/analytics';
import {Box, Divider} from 'native-base';
import {
  Buyers,
  DynamicTextsProps,
  OrderSubstitutionPreference,
} from '../../../../itemDetails/types/itemDetails';
import {
  ItemDetailsAction,
  useCatalogItemToFetchUpdatedQuantity,
  useIsEligibleForAdjacentOOS,
  useItemDetailsContext,
  useUpdateOrderItemQuantity,
} from '@buncha/itemDetails/hooks/ItemDetails';
import React, {useCallback, useMemo, useState} from 'react';
import font, {scaleFonts} from '../../../../appStyles/style/font';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../appStyles/hooks/theme';

import {BottomButton} from '../../../../components/composites/Button';
import BuyerInfoCell from '../../BuyerInfoCell';
import ChatBottomsheet from '../contactBottomSheet/ChatBottomsheet';
import {CustomItemMode} from '../../../../itemDetails/types/CustomItem';
import CustomSubModal from '../CustomSubModal';
import DropItem from '../DropItem/DropItem';
import {DynamicTextType} from '../../../../batchDetails/types';
import {EventType} from '@buncha/config/analytics/type';
import {H6Text} from '../../../../components/core/text';
import {HBox} from '@buncha/components/core/view';
import If from '../../../../components/conditional/If';
import {ItemDetailsScreen} from '../../../../itemDetails/navigation/types';
import ItemNotesModal from '../itemNotesModal';
import {Modules} from '@buncha/utils/types';
import {OrderItem} from '@buncha/home/types';
import {OrderItem as OrderItemScanner} from '@buncha/itemDetails/types/ItemScanner';
import SemiCircularDivider from '../../SemiCircularDivider';
import {ShopperScreens} from '../../../../home/navigation/types';
import {SubstituteItemDetailsAction} from '@buncha/itemDetails/hooks/SubstituteItemDetail';
import {SubstitutionChoice} from '../../../../itemDetails/types/itemReplacement';
import SubstitutionInfoBanner from '../SubstitutionInfo/SubstitutionInfoBanner';
import {Touchable} from '@buncha/components/core/touchable';
import UpdateQuantitySheet from '../UpdateQuantitySheet';
import {navigation} from '../../../../config/navigation';
import {smartLook} from '@buncha/config/analytics';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface RouteParamsType {
  runId?: number;
  storeIndex?: string;
  storeId?: number;
}
interface BuyerInfoProps {
  buyers: Buyers;
  isMenu: boolean;
  isCustomItem: boolean;
  chatEnable: boolean;
  dispatch?: React.Dispatch<any> | null;
  item?: OrderItemScanner;
  isOosSubstitute?: boolean;
  refreshBuyers: () => Promise<void>;
  showDivider?: boolean;
}

const BuyerInfo = (props: BuyerInfoProps) => {
  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const runId = routeParams?.runId || 0;
  const orderId = props?.buyers?.orderId;
  const catalogItemId = props?.item?.id;
  const {state} = useItemDetailsContext();
  const theme = useColorThemeType('main');
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [contactSheetProps, setContactSheetProps] = useState<
    DynamicTextsProps | undefined
  >(undefined);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDropModal, setShowDropModal] = useState(false);
  const [showOOSDropModal, setShowOOSDropModal] = useState(false);
  const [showCustomSubModal, setShowCustomSubModal] = useState(false);
  const [showQtyUpdateSheet, setShowQtyUpdateSheet] = useState(false);
  const [isPermanentlyDropped, setIsPermanentlyDropped] = useState(false);
  const orderItemId = Number(props?.buyers.orderItemId) || 0;
  const toggleContactSheet = useCallback(() => {
    if (contactSheetProps) return setContactSheetProps(undefined);
    setShowContactSheet(!showContactSheet);
  }, [contactSheetProps, showContactSheet]);
  const toggleNotesModal = useCallback(() => {
    setShowNotesModal(!showNotesModal);
  }, [showNotesModal]);
  const muliFont = useFontTheme('muli');
  const storeId = routeParams?.storeId ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);
  const {body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const orderItemAttribute = props?.buyers?.orderItemAttribute;

  const {updateOrderItemQuantity, info} = useUpdateOrderItemQuantity();
  const {checkAdjacentOOS, adjacentOOSData} = useIsEligibleForAdjacentOOS();
  const {getCatalogItemToFetchUpdatedQty} =
    useCatalogItemToFetchUpdatedQuantity(props?.dispatch || null);
  const substitutionChoice = props?.buyers?.substitutionChoice;
  const hasSubstitute = props?.buyers?.substitutionOptions?.length > 0;
  const substitutionPreference = props?.buyers?.order?.substitutionPreference;

  const toggleCustomModal = useCallback(() => {
    setShowCustomSubModal(!showCustomSubModal);
  }, [showCustomSubModal]);

  const navigateToCustom = useCallback(() => {
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.CustomItem,
      params: {
        orderId,
        orderItemId,
        mode: CustomItemMode.substituteItem,
        maxQuantity: props?.buyers?.quantity,
        runId: runId,
      },
    });
  }, [orderId, orderItemId, props?.buyers?.quantity, runId]);

  const handelSubstituteNav = useCallback(() => {
    if (props?.isMenu) return toggleCustomModal();
    const buyerChatInfo = props.buyers.order?.buyerChatInfo;
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ItemReplacement,
      params: {
        orderItemId: orderItemId,
        runId: runId,
        orderId: orderId,
        storeId,
        storeIndex: routeParams?.storeIndex,
        hasSubstitute: hasSubstitute,
        substitutionChoice,
        conversationId: buyerChatInfo?.conversationId,
        isMeijerStore,
        buyer: {
          id: props.buyers.id,
          phoneNumber: props.buyers.phoneNumber,
        },
        phoneNumber: props.buyers.phoneNumber,
        name:
          (props?.buyers?.firstName || '') +
          ' ' +
          (props?.buyers?.lastName?.[0] || ''),
      },
    });
  }, [
    hasSubstitute,
    isMeijerStore,
    orderId,
    orderItemId,
    props.buyers?.firstName,
    props.buyers.id,
    props.buyers?.lastName,
    props.buyers.order?.buyerChatInfo,
    props.buyers.phoneNumber,
    props?.isMenu,
    routeParams?.storeIndex,
    runId,
    storeId,
    substitutionChoice,
    toggleCustomModal,
  ]);

  const handleContactSheetClose = useCallback((func: Function) => {
    return () => {
      setContactSheetProps(undefined);
      setShowContactSheet(false);
      func();
    };
  }, []);

  const getSubstitutionType = useCallback(() => {
    if (isMeijerStore && props?.buyers?.substitutionOptions?.length)
      return DynamicTextType.recommendSubs;
    switch (substitutionChoice) {
      case SubstitutionChoice.byBuyer:
        if (props?.buyers?.substitutionOptions?.length)
          return DynamicTextType.recommendSubs;
        return DynamicTextType.addSubstitute;
      case SubstitutionChoice.noSubstitution:
        return DynamicTextType.noSubs;
      case SubstitutionChoice.byShopper:
        return DynamicTextType.addSubstitute;
      case SubstitutionChoice.none:
        return DynamicTextType.addSubstitute;
      default:
        return DynamicTextType.addSubstitute;
    }
  }, [
    isMeijerStore,
    props?.buyers?.substitutionOptions?.length,
    substitutionChoice,
  ]);

  const handleSubstitution = useCallback(() => {
    const params: DynamicTextsProps = {
      buttonText:
        (substitutionChoice === SubstitutionChoice.byBuyer || isMeijerStore) &&
        props?.buyers?.substitutionOptions?.length
          ? button?.viewSubstitute
          : button?.addSubstitute,
      onButtonClick: handleContactSheetClose(handelSubstituteNav),
      type: [getSubstitutionType()],
    };
    setContactSheetProps(params);
  }, [
    button?.addSubstitute,
    button?.viewSubstitute,
    getSubstitutionType,
    handelSubstituteNav,
    handleContactSheetClose,
    isMeijerStore,
    props?.buyers?.substitutionOptions?.length,
    substitutionChoice,
  ]);

  const handleOnDropClose = useCallback(async () => {
    if (
      orderItemId === state.item?.id ||
      props?.isOosSubstitute ||
      isMeijerStore
    ) {
      setShowDropModal(true);
      setIsPermanentlyDropped(true);
      if (!props?.isOosSubstitute) toggleContactSheet();
    } else {
      const res = await checkAdjacentOOS({
        runId: runId,
        orderId: orderId,
        orderItemId: orderItemId,
        catalogItemId: state.item?.id || 0,
      });
      toggleContactSheet();
      setShowDropModal(!res);
      setShowOOSDropModal(Boolean(res?.isEligible));
    }
    setIsPermanentlyDropped(true);
  }, [
    checkAdjacentOOS,
    isMeijerStore,
    orderId,
    orderItemId,
    props?.isOosSubstitute,
    runId,
    state.item?.id,
    toggleContactSheet,
  ]);

  const handleShowContactSheet = useCallback(async () => {
    if (props.isOosSubstitute) return handleOnDropClose();

    const params: DynamicTextsProps = {
      buttonText:
        substitutionChoice === SubstitutionChoice.byBuyer &&
        props?.buyers?.substitutionOptions?.length
          ? button?.viewSubstitute
          : button?.addSubstitute,
      onButtonClick: handleContactSheetClose(handelSubstituteNav),
      type: [getSubstitutionType()],
    };
    setContactSheetProps(params);
  }, [
    button?.addSubstitute,
    button?.viewSubstitute,
    getSubstitutionType,
    handelSubstituteNav,
    handleContactSheetClose,
    handleOnDropClose,
    props?.buyers?.substitutionOptions?.length,
    props.isOosSubstitute,
    substitutionChoice,
  ]);

  const toggleEditQuantity = useCallback(() => {
    setShowQtyUpdateSheet(!showQtyUpdateSheet);
  }, [showQtyUpdateSheet]);

  const handleDropModal = useCallback((value: boolean) => {
    setShowDropModal(value);
    if (!value) setIsPermanentlyDropped(false);
  }, []);

  const handleUpdateQuantity = useCallback(
    async (updatedQty: number) => {
      Analytics.eventWithProps('Update quantity', {
        type: EventType.input,
        runId,
        orderItemId: orderItemId,
        catalogItemId: props?.item?.catalogItemId,
        previousQuantity:
          props?.buyers?.updatedQuantity || props?.buyers?.quantity,
        quantity: updatedQty,
        storeType: getStoreName(String(storeId)),
        itemName: props?.item?.name,
        client: getClientType(isMeijerStore),
        source: 'Item Detail Page',
      });
      await updateOrderItemQuantity(
        {
          runId,
          orderItemId: orderItemId,
          itemQuantity: Number(updatedQty),
        },
        async (updateOrderItemQuantityRes: OrderItem) => {
          if (
            updateOrderItemQuantityRes &&
            props?.dispatch &&
            !props?.isOosSubstitute
          ) {
            await getCatalogItemToFetchUpdatedQty(orderItemId, runId);
            props.dispatch(
              ItemDetailsAction.updateQtyByOrderItemId({
                orderItemId: orderItemId,
                updatedQty:
                  updateOrderItemQuantityRes?.quantity || Number(updatedQty),
              }),
            );
          }
          if (
            updateOrderItemQuantityRes &&
            props?.dispatch &&
            props?.isOosSubstitute
          ) {
            const totalQuantity = await getCatalogItemToFetchUpdatedQty(
              orderItemId,
              runId,
            );
            props.dispatch(
              SubstituteItemDetailsAction.updateQtyByOrderItemId({
                orderItemId: orderItemId,
                updatedQty:
                  updateOrderItemQuantityRes?.quantity || Number(updatedQty),
              }),
            );
            if (totalQuantity)
              props.dispatch(
                SubstituteItemDetailsAction.setItemDataWithUpdatedQuantity({
                  totalQty: totalQuantity,
                }),
              );
          }
        },
      );
      toggleEditQuantity();
    },
    [
      getCatalogItemToFetchUpdatedQty,
      isMeijerStore,
      orderItemId,
      props,
      runId,
      storeId,
      toggleEditQuantity,
      updateOrderItemQuantity,
    ],
  );

  const customerTypeString = useMemo(() => {
    if (props.buyers?.hasBunchaClub) return body?.customerType?.hasBunchaClub;
    else if (props?.buyers?.order?.buyer?.isVip)
      return body?.customerType?.isVIP;
    else if (props.buyers?.hasNewCustomer)
      return body?.customerType?.newCustomer;
    return '';
  }, [
    body?.customerType?.hasBunchaClub,
    body?.customerType?.isVIP,
    body?.customerType?.newCustomer,
    props.buyers?.hasBunchaClub,
    props.buyers?.hasNewCustomer,
    props.buyers?.order?.buyer?.isVip,
  ]);

  const substituteBannerHandler = useCallback(() => {
    const eventParams = {
      type: EventType.cta,
      isCustomItem: props?.isCustomItem || false,
      substitutionChoice: substitutionChoice,
      substitutionPreference: substitutionPreference,
      orderItemId: props?.item?.id,
      runId: runId,
      client: getClientType(isMeijerStore),
    };
    Analytics.eventWithProps(
      'Item Unavailable Flow (Substitution/Drop Flow)',
      eventParams,
      AnalyticsPlatform.all,
    );
    smartLook.trackCustomEvent(
      'Item Unavailable Flow (Substitution/Drop Flow)',
      eventParams,
    );
    if (
      props?.isCustomItem ||
      ((substitutionChoice === SubstitutionChoice.noSubstitution ||
        (substitutionPreference === OrderSubstitutionPreference.NOT_ALLOWED &&
          substitutionChoice === SubstitutionChoice.none)) &&
        !hasSubstitute)
    )
      return handleDropModal(true);
    return handelSubstituteNav();
  }, [
    handelSubstituteNav,
    handleDropModal,
    hasSubstitute,
    props?.isCustomItem,
    props?.item?.id,
    runId,
    substitutionChoice,
    substitutionPreference,
  ]);

  return (
    <Box>
      <Box style={[styles.Container]}>
        <HBox>
          <BuyerInfoCell
            customerType={customerTypeString}
            buyer={props.buyers}
            isNSC2Item={state?.item?.isNSC2Item}
            orderItemAttribute={orderItemAttribute}
            isVip={props?.buyers?.order?.buyer?.isVip}
            containerStyle={styles.buyerContainer}
          />
          <If condition={!props.isOosSubstitute}>
            <Box style={[styles.jcc]}>
              <BottomButton
                title={'Contact'}
                style={[styles.buttonStyle]}
                titleStyle={[styles.nameTypo]}
                onPress={handleSubstitution}
              />
            </Box>
          </If>
        </HBox>
        <HBox
          style={[
            styles.editQtyBox,
            {backgroundColor: theme?.background.gray[200]},
          ]}>
          <H6Text
            numberOfLines={2}
            style={[
              styles.childTypo,
              {
                fontFamily: muliFont[600].normal,
                lineHeight: scaleFonts(18),
              },
            ]}>
            {body?.editQuantity}
            {props?.buyers?.updatedQuantity || props?.buyers?.quantity}
            <If condition={props?.buyers?.updatedQuantity}>
              {` | Original Qty: ${props?.buyers?.quantity}`}
            </If>
          </H6Text>
          <Touchable
            onPress={toggleEditQuantity}
            style={[
              styles.editQtyContainer,
              {backgroundColor: theme?.background.gray[200]},
            ]}>
            <H6Text
              style={[
                styles.editQyt,
                {
                  color: theme?.text.blue[100],
                  fontFamily: muliFont[700].normal,
                },
              ]}>
              edit quantity
            </H6Text>
          </Touchable>
        </HBox>
      </Box>
      <If condition={props?.isOosSubstitute}>
        <Divider
          style={{
            backgroundColor: theme?.background?.gray?.[200],
          }}
        />
      </If>
      <If condition={!props?.isOosSubstitute}>
        <If condition={props?.buyers?.substitutionNote}>
          <H6Text
            style={[
              styles.childTypo,
              styles.textStyle,
              {
                color: theme?.text?.orange[100],
                fontFamily: font.fonts?.muliSemiBold,
              },
            ]}>
            {body?.notesAdded}
            {props.buyers?.firstName}:{' '}
            <H6Text
              style={[
                {
                  color: theme?.text?.blue[300],
                  fontFamily: font.fonts?.muliSemiBold,
                },
              ]}>
              {props?.buyers?.substitutionNote?.trim()}
            </H6Text>
          </H6Text>
        </If>
      </If>
      <If condition={!props?.isOosSubstitute}>
        <Divider style={[styles.divider]} />
        <SubstitutionInfoBanner
          onPress={substituteBannerHandler}
          substitutionChoice={substitutionChoice}
          hasSubstitutionOptions={hasSubstitute}
          substitutionPreference={substitutionPreference}
          isCustomItem={props?.isCustomItem}
        />
      </If>
      <DropItem
        setShowModal={handleDropModal}
        showModal={showDropModal}
        orderItemId={orderItemId}
        isPermanentlyDropped={isPermanentlyDropped}
        showContactSheet={handleShowContactSheet}
        showOOSModal={showOOSDropModal}
        setShowOOSModal={setShowOOSDropModal}
        runId={runId}
        substitutionChoice={substitutionChoice}
        isAdjacentOOSItem={props?.isOosSubstitute}
        refreshBuyers={props.refreshBuyers}
        isB2BStore={isMeijerStore}
        adjacentOOSData={adjacentOOSData}
        catalogItemId={catalogItemId}
        orderId={orderId}
        isCustomItem={props.isCustomItem}
      />
      <If condition={showContactSheet || Boolean(contactSheetProps)}>
        <ChatBottomsheet
          isOpen={showContactSheet || Boolean(contactSheetProps)}
          onClose={toggleContactSheet}
          runId={runId}
          orderId={orderId}
          storeId={storeId}
          orderItemId={orderItemId}
          isB2BStore={isMeijerStore}
        />
      </If>
      <UpdateQuantitySheet
        open={showQtyUpdateSheet}
        onClose={toggleEditQuantity}
        itemName={props?.item?.name}
        itemImageUrl={props?.item?.itemImageUrl}
        itemPrice={props?.item?.price}
        quantity={props?.buyers?.updatedQuantity || props?.buyers?.quantity}
        handleUpdateQuantity={handleUpdateQuantity}
        loading={info?.loading}
      />
      <ItemNotesModal
        isOpen={showNotesModal}
        onClose={toggleNotesModal}
        orderItemId={orderItemId}
      />
      <CustomSubModal
        isOpen={showCustomSubModal}
        onClose={toggleCustomModal}
        onSubmit={navigateToCustom}
      />
      <If condition={props?.showDivider}>
        <SemiCircularDivider />
      </If>
    </Box>
  );
};

export default React.memo(BuyerInfo);
